import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdCommandAlkonConnection } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';

import { FeatureFlags } from '~constants/featureFlags';
import { CommandAlkonConnection } from '~hooks/useCommandAlkon/models';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { RARELY_CHANGED_DATA_STALE_TIME_IN_MS } from './constants';

const COMMAND_ALKON_CONNECTION_QUERY_KEY = 'commandAlkonConnection';

export const getCommandAlkonConnectionQueryKey = (companyId: string) => [
  COMMAND_ALKON_CONNECTION_QUERY_KEY,
  companyId,
];

async function fetchCommandAlkonConnection(companyId: string, signal: AbortSignal) {
  try {
    const response = await getV1CompaniesCompanyIdCommandAlkonConnection({
      path: { 'company-id': companyId },
      signal,
    });

    return CommandAlkonConnection.parse(response.data.data);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    if (signal.aborted) {
      throw error;
    }

    reportRequestError(
      error,
      t('error_messages.command_alkon.failed_to_fetch_connection'),
    );

    throw error;
  }
}

export default function useCommandAlkonConnectionQuery() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const isFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.commandAlkonIntegration,
  });
  const { canManageCommandAlkonIntegration } = userStore.getPermissions();
  const canQueryCommandAlkonConnection =
    isFeatureFlagEnabled && canManageCommandAlkonIntegration;

  const { data, isError, isLoading } = useQuery({
    queryKey: getCommandAlkonConnectionQueryKey(companyId),
    queryFn: canQueryCommandAlkonConnection
      ? ({ signal }) => fetchCommandAlkonConnection(companyId, signal)
      : skipToken,
    retry: false,
    staleTime: RARELY_CHANGED_DATA_STALE_TIME_IN_MS,
  });

  return {
    commandAlkonConnection: data ?? null,
    isCommandAlkonConnected: Boolean(data),
    isError,
    isLoading,
  };
}
